import {PreviewPosition} from 'components/customer/Preview3D/types';

interface ToggleLabelMeasurements {
    backWall: boolean;
    frontWall: boolean;
    leftWall: boolean;
    rightWall: boolean;
    rightWidthWall: boolean;
    leftWidthWall: boolean;
    leftDepthWall: boolean;
    rightDepthWall: boolean;
    rightWidth2Wall: boolean;
    leftWidth2Wall: boolean;
}

/**
 * Determines which wall measurements and labels should be visible based on the current view position.
 * This function is crucial for maintaining readability in 3D room planning by hiding measurements
 * that would be obscured or confusing from certain camera angles.
 *
 * @param {boolean} isForPrinting - Whether the view is for printing (affects visibility rules)
 * @param {PreviewPosition} previewPosition - The current camera/view position
 * @return {ToggleLabelMeasurements} Object indicating which walls should show their measurements and labels
 */
export const canShowLabelAndMeasurements = (
    isForPrinting: boolean,
    previewPosition: PreviewPosition
): ToggleLabelMeasurements => {
    // If not for printing, show all measurements for full visibility
    if (!isForPrinting) {
        return {
            backWall: true,
            frontWall: true,
            leftWall: true,
            rightWall: true,
            leftWidthWall: true,
            rightWidthWall: true,
            leftDepthWall: true,
            rightDepthWall: true,
            rightWidth2Wall: true,
            leftWidth2Wall: true,
        };
    }

    // Define which walls should be hidden for each view position
    const hiddenWallsByView: Partial<
        Record<PreviewPosition, (keyof ToggleLabelMeasurements)[]>
    > = {
        // Front view - original logic that was working correctly for L-shaped rooms
        FRONT: [
            'frontWall',
            'rightWall',
            'leftDepthWall',
            'rightDepthWall',
            'rightWidth2Wall',
            'leftWidth2Wall',
        ],

        // Back view - hide back wall and side walls that would be behind
        BACK: [
            'backWall',
            'leftWall',
            'rightWall',
            'leftWidthWall',
            'rightWidthWall',
            'leftDepthWall',
            'rightDepthWall',
            'leftWidth2Wall',
        ],

        // Left view - hide left wall and walls that would be behind, show right wall (opposite side)
        LEFT: [
            'leftWall',
            'frontWall',
            'backWall',
            'leftWidthWall',
            'leftDepthWall',
            'rightWidth2Wall',
        ],

        // Right view - hide right wall and all right-facing labels
        RIGHT: [
            'rightWall',
            'frontWall',
            'backWall',
            'rightWidthWall',
            'rightDepthWall',
            'rightWidth2Wall',
            'leftDepthWall',
        ],

        // Left back corner view - hide walls that would be behind
        LEFT_BACK: [
            'frontWall',
            'rightWall',
            'rightWidthWall',
            'rightDepthWall',
        ],

        // Right back corner view - hide walls that would be behind
        RIGHT_BACK: ['frontWall', 'leftWall', 'leftWidthWall', 'leftDepthWall'],

        // Left leveled view - fixed logic to show appropriate walls
        LEFT_LEVELED: [
            'frontWall',
            'leftWall',
            'leftDepthWall',
            'leftWidthWall',
            'rightWidth2Wall',
        ],

        // Right leveled view - fixed logic to show appropriate walls
        RIGHT_LEVELED: [
            'rightWall',
            'backWall',
            'rightWidthWall',
            'rightDepthWall',
            'leftWidth2Wall',
            'rightWidth2Wall',
        ],

        // Left leveled static view - hide walls that would be behind
        LEFT_LEVELED_STATIC: [
            'frontWall',
            'leftWall',
            'backWall',
            'leftWidthWall',
            'leftDepthWall',
        ],

        // Top view - show all walls for comprehensive view
        TOP: [],

        // Top view without editing - show all walls
        TOP_WITHOUT_EDITING: [],

        // PDF view - show all walls for documentation
        PDF_VIEW: [],
    };

    // Start with all walls visible
    const allWalls: ToggleLabelMeasurements = {
        backWall: true,
        frontWall: true,
        leftWall: true,
        rightWall: true,
        leftWidthWall: true,
        rightWidthWall: true,
        leftDepthWall: true,
        rightDepthWall: true,
        rightWidth2Wall: true,
        leftWidth2Wall: true,
    };

    // Hide walls based on the current view position
    // eslint-disable-next-line security/detect-object-injection -- Object key is from a predefined enum, not user input
    const hidden = hiddenWallsByView[previewPosition] ?? [];

    hidden.forEach((wall) => {
        // eslint-disable-next-line security/detect-object-injection -- Object key is from a predefined enum, not user input
        if (wall in allWalls) {
            // eslint-disable-next-line security/detect-object-injection -- Object key is from a predefined enum, not user input
            allWalls[wall] = false;
        }
    });

    return allWalls;
};
